import { Button, Select } from '@nazka/nazka.mapframe.components';
import { useForm, Controller } from 'react-hook-form';

import { useTranslation } from '@utils/i18n/i18n.consumers';
import DynamicArrayInput from '@App/components/DynamicArrayInput/DynamicArrayInput';

import { HAZARD_CATEGORIES } from '../../admin.constants';

import './admin-edit-plan-form.styl';

function AdminEditPlanForm({
  plan,
  onSuccess,
  onCancel,
  isPending,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    control,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      title: plan.translations?.en?.title || '',
      description: plan.translations?.en?.description || '',
      hazardCategories: plan.hazardCategories.map(id => HAZARD_CATEGORIES.find(c => c.id === id)) || [],
      plan: plan.translations?.en?.plan || [],
      stakeholders: plan.translations?.en?.stakeholders || '',
      challenges: plan.translations?.en?.challenges || '',
      responsible: plan.responsible || '',
    },
  });

  const { t } = useTranslation();

  const onSubmit = (data) => {
    const updatePayload = {
      translations: {
        en: {
          title: data.title,
          description: data.description,
          plan: data.plan,
          stakeholders: data.stakeholders,
          challenges: data.challenges,
        },
      },
      responsible: data.responsible,
      hazardCategories: data.hazardCategories.map(category => category.id),
    };

    onSuccess(updatePayload);
  };

  return (
    <div className="admin-edit-plan-form">
      <div className="admin-edit-plan-form__content">
        <div className="admin-edit-plan-form__field">
          <label htmlFor="title" className="admin-edit-plan-form__label">
            {`${t('Title')} *`}
          </label>
          <input
            id="title"
            type="text"
            {...register('title', {
              required: t('Title is required.'),
              // minLength: { value: 3, message: 'Title must be at least 3 characters' },
            })}
            className={`admin-edit-plan-form__input ${errors.title ? 'admin-edit-plan-form__input--error' : ''
            }`}
            placeholder="Enter plan title"
          />
          {errors.title && (
            <p className="admin-edit-plan-form__error">{errors.title.message}</p>
          )}
        </div>

        <div className="admin-edit-plan-form__field">
          <label htmlFor="description" className="admin-edit-plan-form__label">
            {`${t('What are you trying to adapt in your community and why is it important?')} *`}
          </label>
          <textarea
            id="description"
            rows={4}
            {...register('description', {
              required: t('Description is required.'),
              // minLength: { value: 10, message: 'Description must be at least 10 characters' },
            })}
            className={`admin-edit-plan-form__textarea ${errors.description ? 'admin-edit-plan-form__textarea--error' : ''}`}
            placeholder="Describe the adaptation plan..."
          />
          {errors.description && (
            <p className="admin-edit-plan-form__error">{errors.description.message}</p>
          )}
        </div>

        <div className="admin-edit-plan-form__field">
          <label htmlFor="hazardCategories" className="admin-edit-plan-form__label">
            {`${t('Linked hazard')} *`}
          </label>
          <Controller
            name="hazardCategories"
            control={control}
            rules={{
              required: t('Please select at least one linked hazard.'),
              validate: (value) => {
                if (!value || value.length === 0) {
                  return t('Please select at least one linked hazard.');
                }

                return true;
              },
            }}
            render={({ field }) => (
              <Select
                className="admin-edit-plan-form__select"
                rootElProps={{ id: 'hazardCategories' }}
                value={field.value}
                onInputChange={(value) => {
                  field.onBlur();
                  field.onChange(value);
                }}
                css={{ borderColor: errors.hazardCategories ? '#dc3545' : '#ddd', color: errors.hazardCategories ? '#dc3545' : '#333' }}
                options={HAZARD_CATEGORIES}
                isMulti
                placeholder={t('Select hazards...')}
              />
            )}
          />
          {errors.hazardCategories && (
            <p className="admin-edit-plan-form__error">{errors.hazardCategories.message}</p>
          )}
        </div>

        <DynamicArrayInput
          name="plan"
          control={control}
          label={t('What is your action plan for adapting it? List the different steps of your plan.')}
          required
          placeholder={t('Add step')}
          addButtonText={t('Add step')}
          minItems={1}
          maxItems={10}
          errors={errors.plan}
        />

        <div className="admin-edit-plan-form__field">
          <label htmlFor="stakeholders" className="admin-edit-plan-form__label">
            {`${t('Which stakeholders need to be involved?')} *`}
          </label>
          <textarea
            id="stakeholders"
            rows={4}
            {...register('stakeholders', {
              required: t('Stakeholders is required.'),
              // minLength: { value: 2, message: t('Stakeholders must be at least 2 characters.') },
            })}
            className={`admin-edit-plan-form__input ${errors.stakeholders ? 'admin-edit-plan-form__input--error' : ''
            }`}
            placeholder={t('Which stakeholders need to be involved?')}
          />
          {errors.stakeholders && (
            <p className="admin-edit-plan-form__error">{errors.stakeholders.message}</p>
          )}
        </div>

        <div className="admin-edit-plan-form__field">
          <label htmlFor="challenges" className="admin-edit-plan-form__label">
            {`${t('What are the challenges to execute the adaptation plan? Can you come up with any solutions for these challenges?')} *`}
          </label>
          <textarea
            id="challenges"
            rows={4}
            {...register('challenges', {
              required: t('Challenges is required.'),
              // minLength: { value: 2, message: t('Challenges must be at least 2 characters.') },
            })}
            className={`admin-edit-plan-form__input ${errors.challenges ? 'admin-edit-plan-form__input--error' : ''
            }`}
            placeholder={t('What are the challenges to execute the adaptation plan? Can you come up with any solutions for these challenges?')}
          />
          {errors.challenges && (
            <p className="admin-edit-plan-form__error">{errors.challenges.message}</p>
          )}
        </div>

        <div className="admin-edit-plan-form__field">
          <label htmlFor="responsible" className="admin-edit-plan-form__label">
            {`${t('Who is responsible for this adaptation plan?')} *`}
          </label>
          <input
            id="responsible"
            type="text"
            {...register('responsible', {
              required: 'Responsible party is required.',
              // minLength: { value: 2, message: 'Responsible party must be at least 2 characters.' },
            })}
            className={`admin-edit-plan-form__input ${errors.responsible ? 'admin-edit-plan-form__input--error' : ''
            }`}
            placeholder={t('Who is responsible for this adaptation plan?')}
          />
          {errors.responsible && (
            <p className="admin-edit-plan-form__error">{errors.responsible.message}</p>
          )}
        </div>

        <div className="admin-edit-plan-form__actions">
          <Button
            type="button"
            onClick={handleSubmit(onSubmit)}
            className="admin-edit-plan-form__button"
            css={{
              color: 'white',
              '&:hover': {
                color: 'white',
              },
            }}
            disabled={!isValid}
            isLoading={isPending}
          >
            {t('Save changes')}
          </Button>
          <Button
            type="button"
            onClick={onCancel}
            className="admin-edit-plan-form__button"
            layout="outlined"
          >
            {t('Cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default AdminEditPlanForm;